import React from 'react';

import Grid from '@material-ui/core/Grid';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {Route, useRouteMatch} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {Icon} from 'components/ui-kit/Icon';
import {
    ContentColumns,
    TitleText,
    Title,
    Id,
    CopyButton,
    StatusWrapper,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {RouterSwitch, RouterTab, RouterTabList} from 'components/ui-kit/RouterTabs';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {colors} from 'theme';
import {UPDReport} from 'types/documents';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {DataTitle} from './styled';
import {UPDProducts} from './UPDProducts';

interface OrdersDetailPageProps {
    data: UPDReport;
}

export function convertData(data: UPDReport, t: TFunction): UPDReport {
    return {
        reportId: handleNil(data?.reportId),
        // t('pages.documents.details.UPD.createdAt')
        createdAt: handleNil(data?.createdAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy, HH:mm'),
        ),
        reportType: data?.reportType,
        status: handleNil(data?.status, (value: string) =>
            t(`dictionaries.documents.status.${value}`),
        ),
        reportInfo: {
            // t('pages.documents.details.UPD.processedAt')
            processedAt: handleNil(data?.reportInfo?.processedAt, (value: string) =>
                formatDate(value, 'dd.MM.yyyy'),
            ),
            // t('pages.documents.details.UPD.buyerLegalName')
            buyerLegalName: handleNil(data?.reportInfo?.buyerLegalName),
            // t('pages.documents.details.UPD.sellerLegalName')
            sellerLegalName: handleNil(data?.reportInfo?.sellerLegalName),
            // t('pages.documents.details.UPD.currency')
            currency: handleNil(data?.reportInfo?.currency),
        },
    };
}

export const UPD = ({data}: OrdersDetailPageProps) => {
    const {t} = useTranslation();
    const {path, url} = useRouteMatch();
    const copy = useClipboard();

    const convertedData = React.useMemo(() => convertData(data, t), [data, t]);

    const copyCode = () => {
        copy(convertedData.reportId);
        toast.success(t('pages.documents.details.idCopied'));
    };

    return (
        <>
            <TitleWrapper>
                <TitleText>
                    <Title>
                        {t('pages.documents.details.UPD.UPDId')} <Id>{convertedData.reportId}</Id>
                    </Title>
                    <CopyButton
                        icon={
                            <Icon
                                source={CopyIcon}
                                width={15}
                                height={15}
                                fillColor={colors.brandPrimary}
                            />
                        }
                        onClick={copyCode}
                        hoverIndication={true}
                        label={t('pages.documents.details.copyId')}
                        labelPlace={'bottom'}
                    />
                </TitleText>
                <StatusWrapper color={DOCUMENT_STATUS_COLORS[data.status ?? 'DEFAULT']}>
                    {convertedData.status}
                </StatusWrapper>
            </TitleWrapper>

            <RouterTabList baseUrl={url}>
                <RouterTab url="">{t('pages.documents.detail.commonInfo')}</RouterTab>
                <RouterTab url="products">{t('pages.documents.detail.products')}</RouterTab>
            </RouterTabList>

            <RouterSwitch rootPath={path}>
                <Route path={`${path}`} exact>
                    <Grid container>
                        <Grid item xs={12} md={4}>
                            <DataTitle>{t('pages.documents.details.UPD.generalInfo')}</DataTitle>
                        </Grid>
                    </Grid>
                    <ContentColumns
                        data={[
                            {
                                createdAt: convertedData.createdAt,
                                processedAt: convertedData.reportInfo.processedAt,
                            },
                            {
                                currency: convertedData.reportInfo.currency,
                            },
                        ]}
                        tNameSpace="pages.documents.details.UPD"
                    />
                    <Grid container>
                        <Grid item xs={12} md={4}>
                            <DataTitle>{t('pages.documents.details.UPD.seller')}</DataTitle>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <DataTitle>{t('pages.documents.details.UPD.buyer')}</DataTitle>
                        </Grid>
                    </Grid>
                    <ContentColumns
                        data={[
                            {
                                sellerLegalName: convertedData.reportInfo.sellerLegalName,
                            },
                            {
                                buyerLegalName: convertedData.reportInfo.buyerLegalName,
                            },
                        ]}
                        tNameSpace="pages.documents.details.UPD"
                    />
                </Route>
                <Route path={`${path}/products`} exact>
                    <UPDProducts reportId={convertedData.reportId} />
                </Route>
            </RouterSwitch>
        </>
    );
};
